import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import {
  Sparkles,
  Image,
  FileText,
  Hash,
  RefreshCw,
  Copy,
  Send,
  Loader2,
  Zap,
} from 'lucide-react';
import { TopNav } from '@/components/layout/TopNav';
import { aiApi, socialAccountsApi, mediaApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { PLATFORMS } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { cn } from '@/lib/utils';

const TEMPLATES = [
  { id: 'marketing_post', icon: Send, label: 'Marketing Post', desc: 'Engaging post for any platform' },
  { id: 'reel_script', icon: FileText, label: 'Reel Script', desc: 'Short-form video script with hook & CTA' },
  { id: 'thought_leadership', icon: Sparkles, label: 'Thought Leadership', desc: 'Long-form LinkedIn/Facebook post' },
  { id: 'tweet', icon: Send, label: 'Tweet', desc: 'Punchy X post under 280 chars' },
  { id: 'shorts_video', icon: FileText, label: 'Shorts Script', desc: 'YouTube Shorts video script' },
  { id: 'hashtags', icon: Hash, label: 'Hashtags', desc: 'Relevant hashtag suggestions' },
  { id: 'repurpose', icon: RefreshCw, label: 'Repurpose Content', desc: 'Adapt existing content for new platforms' },
];

const TONE_OPTIONS = [
  'professional', 'casual', 'funny', 'inspirational', 'educational', 'urgent',
];

const LENGTH_OPTIONS = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' },
];

type RepurposeResult = { platform: string; content: string; error?: string };

// Mirrors the API's input caps (routes/ai.ts) so oversized input is stopped here.
const MAX_PROMPT_CHARS = 4000;
const MAX_SOURCE_CHARS = 12000;
const MAX_IMAGE_PROMPT_CHARS = 2000;

export function AIStudio() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();

  const [activeTemplate, setActiveTemplate] = useState('marketing_post');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [platform, setPlatform] = useState('');
  const [sourceContent, setSourceContent] = useState('');
  const [result, setResult] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [activeTab, setActiveTab] = useState<'text' | 'image'>('text');
  const [repurposeResults, setRepurposeResults] = useState<RepurposeResult[]>([]);

  const { data: accountsData } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const connectedPlatforms = [...new Set(
    (accountsData?.accounts ?? []).map((a) => a.platform)
  )];

  const generate = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return aiApi.generate(token!, {
        workspaceId: currentWorkspace!.id,
        prompt,
        type: activeTemplate,
        platform: platform || undefined,
        tone,
        length: length as 'short' | 'medium' | 'long',
        sourceContent: activeTemplate === 'repurpose' ? sourceContent : undefined,
      });
    },
    // Clear earlier platform variants: the single result only renders when
    // there are none, so a new Generate after "All Platforms" stayed hidden.
    onSuccess: (data) => {
      setResult(data.result);
      setRepurposeResults([]);
    },
  });

  const repurposeAll = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const targetPlatforms = connectedPlatforms.length > 0
        ? connectedPlatforms
        : ['x', 'linkedin', 'instagram', 'facebook'];

      // One model call for the whole set. Per-platform requests repeated the
      // brand prompt and the trend context every time.
      const data = await aiApi.generate(token!, {
        workspaceId: currentWorkspace!.id,
        prompt: prompt || 'Repurpose the source content',
        type: 'repurpose',
        platforms: targetPlatforms,
        tone,
        length: length as 'short' | 'medium' | 'long',
        sourceContent: sourceContent || result || undefined,
      });

      if (data.variants && data.variants.length > 0) {
        return targetPlatforms.map((platform) => {
          const hit = data.variants!.find((v) => v.platform === platform);
          return hit
            ? { platform, content: hit.content }
            : { platform, content: '', error: 'No variant returned' };
        });
      }
      return [{ platform: targetPlatforms[0], content: data.result }];
    },
    onSuccess: (data) => setRepurposeResults(data),
  });

  const generateImage = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return aiApi.generateImage(token!, {
        workspaceId: currentWorkspace!.id,
        prompt: imagePrompt,
      });
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (!currentWorkspace) {
    return (
      <div className="flex-1 overflow-auto">
        <TopNav title="AI Studio" />
        <div className="flex items-center justify-center h-[calc(100vh-3.5rem)]">
          <p className="text-sm text-muted-foreground">Select a workspace to use AI Studio</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      <TopNav title="AI Studio" />

      <div className="flex flex-col md:flex-row flex-1 overflow-y-auto md:overflow-hidden">
        {/* Left sidebar */}
        <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-border flex flex-col md:overflow-y-auto shrink-0">
          <div className="p-4 space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 bg-muted/40 border border-border rounded-lg p-0.5">
              <button
                onClick={() => setActiveTab('text')}
                className={cn(
                  'flex-1 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'text'
                    ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Text
              </button>
              <button
                onClick={() => setActiveTab('image')}
                className={cn(
                  'flex-1 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'image'
                    ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Image
              </button>
            </div>

            {activeTab === 'text' && (
              <>
                {/* Tone */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-2">
                    Tone
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {TONE_OPTIONS.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTone(t)}
                        className={cn(
                          'px-2 py-1 text-xs rounded-md transition-colors capitalize',
                          tone === t
                            ? 'bg-accent/20 text-accent ring-1 ring-accent/40'
                            : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/60 border border-border/60'
                        )}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Length */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-2">
                    Length
                  </label>
                  <div className="flex gap-1">
                    {LENGTH_OPTIONS.map((l) => (
                      <button
                        key={l.value}
                        onClick={() => setLength(l.value)}
                        className={cn(
                          'flex-1 py-1.5 text-xs rounded-md transition-colors border',
                          length === l.value
                            ? 'bg-accent/20 text-accent border-accent/40 ring-1 ring-accent/30'
                            : 'bg-muted/40 text-muted-foreground border-border/60 hover:text-foreground hover:bg-muted/60'
                        )}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Platform */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-2">
                    Platform (optional)
                  </label>
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="w-full bg-input border border-border rounded-md px-2 py-1.5 text-xs text-foreground outline-none"
                  >
                    <option value="">Any platform</option>
                    <option value="x">X (Twitter)</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="tiktok">TikTok</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Center: main area */}
        <div className="flex-1 flex flex-col md:overflow-hidden">
          {activeTab === 'text' ? (
            <>
              {/* Templates */}
              <div className="p-4 border-b border-border">
                <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
                  Templates
                </p>
                <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
                  {TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => { setActiveTemplate(tpl.id); setRepurposeResults([]); }}
                      className={cn(
                        'flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors',
                        activeTemplate === tpl.id
                          ? 'border-accent/50 bg-accent/10 text-accent'
                          : 'border-border bg-card text-muted-foreground hover:text-foreground hover:border-accent/30 hover:bg-muted/20'
                      )}
                    >
                      <tpl.icon className="h-4 w-4 shrink-0" />
                      <span className="text-xs font-medium">{tpl.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Prompt area */}
              <div className="flex-1 flex flex-col p-4 gap-3 overflow-y-auto">
                {activeTemplate === 'repurpose' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">
                      Source content to repurpose
                    </label>
                    <textarea
                      value={sourceContent}
                      maxLength={MAX_SOURCE_CHARS}
                      onChange={(e) => setSourceContent(e.target.value)}
                      placeholder="Paste the original content here..."
                      className="w-full bg-input border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none h-24 focus:ring-1 focus:ring-accent"
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <textarea
                    value={prompt}
                    maxLength={MAX_PROMPT_CHARS}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe what you want to create..."
                    className="w-full sm:flex-1 bg-input border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none h-20 focus:ring-1 focus:ring-accent"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) generate.mutate();
                    }}
                  />
                  <div className="flex flex-col gap-2 self-start">
                    <button
                      onClick={() => generate.mutate()}
                      disabled={!prompt.trim() || generate.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors"
                    >
                      {generate.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      <span className="text-sm">Generate</span>
                    </button>

                    {activeTemplate === 'repurpose' && (
                      <button
                        onClick={() => repurposeAll.mutate()}
                        disabled={(!prompt.trim() && !sourceContent.trim()) || repurposeAll.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-accent/15 text-accent ring-1 ring-accent/40 rounded-lg hover:bg-accent/25 disabled:opacity-50 transition-colors"
                        title={`Generate for all connected platforms (${connectedPlatforms.length > 0 ? connectedPlatforms.join(', ') : 'default platforms'})`}
                      >
                        {repurposeAll.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Zap className="h-4 w-4" />
                        )}
                        <span className="text-sm whitespace-nowrap">All Platforms</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Single result */}
                {result && repurposeResults.length === 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">Result</span>
                      <button
                        onClick={() => copyToClipboard(result)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </button>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{result}</p>
                  </div>
                )}

                {/* Multi-platform repurpose results */}
                {repurposeResults.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Platform Variants
                    </p>
                    {repurposeResults.map((r) => {
                      const platformInfo = PLATFORMS[r.platform as keyof typeof PLATFORMS];
                      return (
                        <div key={r.platform} className="bg-card border border-border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="flex items-center gap-2 text-xs font-medium text-foreground">
                              <PlatformIcon platform={r.platform} size="sm" />
                              {platformInfo?.label ?? r.platform}
                            </span>
                            {!r.error && (
                              <button
                                onClick={() => copyToClipboard(r.content)}
                                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                              >
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                              </button>
                            )}
                          </div>
                          {r.error ? (
                            <p className="text-xs text-destructive">{r.error}</p>
                          ) : (
                            <p className="text-sm text-foreground whitespace-pre-wrap">{r.content}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {(generate.isError || repurposeAll.isError) && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                    <p className="text-xs text-destructive">
                      {(generate.error ?? repurposeAll.error)?.message ||
                        'Generation failed. Please try again.'}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Image generation tab */
            <div className="flex-1 flex flex-col p-4 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-2">
                  Image Prompt
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <textarea
                    value={imagePrompt}
                    maxLength={MAX_IMAGE_PROMPT_CHARS}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    placeholder="Describe the image you want to generate..."
                    className="w-full sm:flex-1 bg-input border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none h-24 focus:ring-1 focus:ring-accent"
                  />
                  <button
                    onClick={() => generateImage.mutate()}
                    disabled={!imagePrompt.trim() || generateImage.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors self-start"
                  >
                    {generateImage.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Image className="h-4 w-4" />
                    )}
                    <span className="text-sm">Generate</span>
                  </button>
                </div>
              </div>

              {generateImage.isSuccess && generateImage.data?.r2Key && (
                <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                  <img
                    src={mediaApi.serveUrl(generateImage.data.r2Key)}
                    alt="AI generated"
                    className="max-h-96 rounded-lg border border-border"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-success">✓ Saved to the media library</p>
                    <a
                      href={`/publishing?compose=true&media=${encodeURIComponent(generateImage.data.r2Key)}`}
                      className="text-xs font-medium text-accent hover:underline"
                    >
                      Use in post →
                    </a>
                  </div>
                </div>
              )}

              {generateImage.isError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                  <p className="text-xs text-destructive">
                    {generateImage.error?.message || 'Image generation failed. Please try again.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
